import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button,
  Divider, alpha, Stack, Link, ToggleButton, ToggleButtonGroup, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimerIcon from '@mui/icons-material/Timer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getLiveMinutes } from '../services/api';
import { formatDuration } from './LiveMinutesSearch';

const THEME_COLOR = '#00897b';
const BAR_COLOR = '#00897b';


// A precise "Hh MMm SSs" duration for the per-line list, where a few seconds
// still matters. Falls back to an em dash for null / non-numeric.
function formatPreciseDuration(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return null;
  const total = Math.round(Number(seconds));
  if (total <= 0) return null;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 && h === 0) parts.push(`${s}s`);
  return parts.join(' ') || `${total}s`;
}

// Display label for an item: prefer heading, fall back to text, then item ref.
function itemLabel(item) {
  return item.heading || item.text || item.item_number_raw || 'Item';
}

export default function LiveMinutesDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartSort, setChartSort] = useState('order'); // 'order' | 'duration'

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getLiveMinutes(id);
        setDoc(data);
      } catch (err) {
        console.error('Error fetching live minutes:', err);
        setError('Failed to load live minutes');
      }
      setLoading(false);
    };
    fetchDoc();
  }, [id]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const items = useMemo(() => (Array.isArray(doc?.items) ? doc.items : []), [doc]);

  // Program (business) items that actually have a positive business duration —
  // these power the showcase bar chart. Items with null / 0 / negative
  // business_item_duration_seconds (data quirks) are excluded from the chart
  // but still shown in the chronological list below.
  const chartData = useMemo(() => {
    const rows = items
      .filter((it) => it.is_program_item && Number(it.business_item_duration_seconds) > 0)
      .map((it) => {
        const label = itemLabel(it);
        const durationLabel = formatDuration(it.business_item_duration_seconds);
        return {
          seq: it.seq,
          ref: it.item_number_raw || '',
          label,
          seconds: Number(it.business_item_duration_seconds),
          durationLabel,
        };
      });
    if (chartSort === 'duration') {
      rows.sort((a, b) => b.seconds - a.seconds);
    } else {
      rows.sort((a, b) => a.seq - b.seq);
    }
    return rows;
  }, [items, chartSort]);

  // Chronological list, sorted by sequence (the canonical document order).
  const orderedItems = useMemo(
    () => [...items].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0)),
    [items]
  );

  // Largest business-item duration — used to scale the horizontal bars.
  const maxChartSeconds = useMemo(
    () => chartData.reduce((m, r) => Math.max(m, r.seconds), 0) || 1,
    [chartData]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  if (error || !doc) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Document not found'}</Typography>
        <Button component={RouterLink} to="/live-minutes" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Live Minutes
        </Button>
      </Box>
    );
  }

  const sittingWindow = (() => {
    const fmt = (s) => {
      if (!s) return null;
      const d = new Date(s);
      return d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
    };
    const start = fmt(doc.sitting_start);
    const end = fmt(doc.sitting_end);
    if (start && end) return `${start} – ${end}`;
    return start || end || null;
  })();


  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to={`/live-minutes${doc.chamber ? `?chamber=${encodeURIComponent(doc.chamber)}` : ''}`}
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Live Minutes
        </Button>
      </Box>

      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(THEME_COLOR, 0.08)} 0%, ${alpha(THEME_COLOR, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(THEME_COLOR, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <TimerIcon sx={{ fontSize: 48, color: THEME_COLOR }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: THEME_COLOR, mb: 0.5 }}>
              {doc.chamber || 'Live Minutes'}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {formatDate(doc.date) || doc.date}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {sittingWindow && (
                <Chip icon={<AccessTimeIcon />} label={sittingWindow} size="small" sx={{ bgcolor: THEME_COLOR, color: 'white', '& .MuiChip-icon': { color: 'white' } }} />
              )}
              {doc.total_duration_seconds != null && (
                <Chip label={`Total: ${formatDuration(doc.total_duration_seconds)}`} size="small" variant="outlined" />
              )}
              {doc.item_count != null && (
                <Chip label={`${doc.item_count} items`} size="small" variant="outlined" />
              )}
              {doc.program_item_count != null && (
                <Chip label={`${doc.program_item_count} business items`} size="small" variant="outlined" />
              )}
            </Stack>

            {doc.source_url && (
              <Button
                size="small"
                variant="outlined"
                href={doc.source_url}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
              >
                View on APH
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Per-business-item duration showcase */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR }}>
            Time spent per business item
          </Typography>
          <ToggleButtonGroup
            value={chartSort}
            exclusive
            size="small"
            onChange={(e, v) => v && setChartSort(v)}
          >
            <ToggleButton value="order" sx={{ textTransform: 'none' }}>In order</ToggleButton>
            <ToggleButton value="duration" sx={{ textTransform: 'none' }}>Longest first</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {chartData.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No per-item timing is available for this sitting day yet.
          </Typography>
        ) : (
          // Horizontal bar / timeline — one row per business item, each bar width
          // proportional to its share of the longest item. Rendered with native
          // MUI/CSS rather than a charting lib so it copes with long and
          // duplicated headings and any data quirk, and stays robust across
          // dependency upgrades.
          <Stack spacing={0.75}>
            {chartData.map((row) => {
              const pct = Math.max(2, Math.round((row.seconds / maxChartSeconds) * 100));
              return (
                <Box key={`${row.seq}-${row.ref}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Tooltip title={row.label} placement="top-start">
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ width: { xs: 120, sm: 220 }, flexShrink: 0, color: 'text.secondary' }}
                    >
                      {row.ref ? `${row.ref}  ` : ''}{row.label}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        height: 16,
                        width: `${pct}%`,
                        minWidth: 4,
                        bgcolor: BAR_COLOR,
                        borderRadius: 1,
                        transition: 'width 0.3s ease',
                      }}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                      {row.durationLabel}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>

      {/* Chronological items list */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: THEME_COLOR, mb: 2 }}>
          Chronological record
        </Typography>

        {orderedItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No items recorded for this sitting day.
          </Typography>
        ) : (
          <Box>
            {orderedItems.map((item, idx) => {
              const isSub = !item.is_program_item && item.parent_item_number;
              const dur = formatPreciseDuration(item.duration_seconds);
              const label = item.heading;
              const text = item.text;
              return (
                <Box key={`${item.seq}-${item.object_id || idx}`}>
                  {idx > 0 && <Divider sx={{ my: 0.5 }} />}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      py: 1,
                      pl: isSub ? 4 : 0,
                      borderLeft: isSub ? `2px solid ${alpha(THEME_COLOR, 0.3)}` : 'none',
                      ml: isSub ? 1 : 0,
                    }}
                  >
                    {/* Time gutter */}
                    <Box sx={{ minWidth: 64, flexShrink: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {item.time_raw || ''}
                      </Typography>
                    </Box>

                    {/* Body */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                        {item.item_number_raw && (
                          <Typography component="span" variant="caption" sx={{ color: THEME_COLOR, fontWeight: 700 }}>
                            {item.item_number_raw}
                          </Typography>
                        )}
                        {label && (
                          <Typography component="span" sx={{ fontWeight: item.is_program_item ? 600 : 500 }}>
                            {label}
                          </Typography>
                        )}
                        {item.is_program_item && (
                          <Chip label="Business item" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        {item.is_bill && (
                          <Chip label="Bill" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: alpha(THEME_COLOR, 0.15) }} />
                        )}
                        {item.status && (
                          <Chip label={item.status} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>

                      {text && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}
                        >
                          {item.is_bill && item.bill_id ? (
                            <>
                              <Link
                                component={RouterLink}
                                to={`/bills/${encodeURIComponent(item.bill_id)}`}
                                underline="hover"
                                sx={{ color: THEME_COLOR, fontWeight: 500 }}
                              >
                                {text}
                              </Link>
                            </>
                          ) : (
                            text
                          )}
                        </Typography>
                      )}
                    </Box>

                    {/* Duration */}
                    <Box sx={{ minWidth: 72, flexShrink: 0, textAlign: 'right' }}>
                      {dur && (
                        <Chip
                          label={dur}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.72rem',
                            bgcolor: alpha(THEME_COLOR, item.is_program_item ? 0.18 : 0.08),
                            color: 'text.primary',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
